import { memo, useContext, useMemo } from 'react';
import { determineLender, getObfuscatedCredit } from '@atc/wallet-payment-fns';
import { financingDisclaimers } from 'axl-config';
import { Box, LoadingSpinner } from 'reaxl';
import { LenderPayment } from 'reaxl-wallet';
import ConfigContext from '../../ConfigContext/ConfigContext.js';
import Disclaimer from '../../Disclaimer/Disclaimer.js';
import DisclaimerWithModalContent from '../../DisclaimerWithModalContent/DisclaimerWithModalContent.js';
import LendingOptionsModal from '../../LendingOptionsModal/LendingOptionsModal.js';
import OfferBreakdownModal from '../../OfferBreakdownModal/OfferBreakdownModal.js';
import OfferBreakdown from '../../OfferBreakdown/OfferBreakdown.js';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
function OfferColumn(_ref) {
  let {
    lendingOptions,
    container,
    ctas,
    customRate,
    dealerFinancingCTA,
    disclaimerProps,
    dueAtSigningValue,
    financeDownPayment,
    financingCallToAction,
    handleFallbackPayment,
    handleLendingOptionsSelection,
    isLoading,
    isPennyPerfectPayment,
    listing,
    listingPaymentDetails,
    localInterestRate,
    localPrice,
    offerBreakdownProps,
    offerPrice,
    paymentCalculationDetails,
    renderTaxesAndFees,
    selectedCreditTier,
    selectedLeaseTerm,
    selectedLoanTerm,
    selectedMileage,
    setShowLendingOptionsModal,
    showLendingOptionsModal,
    setShowOfferBreakdownModal,
    showOfferBreakdownModal,
    hasTaxesFeesModalOpen,
    tabName,
    totalCashOut,
    totalEquity,
    totalFinancing,
    totalTax,
    useCustomRate,
    vehicleCondition,
    paymentErrorState,
    withUDSRedesignRollout = false
  } = _ref;
  const {
    digitalRetailingType,
    showLenderDisclaimer,
    showLenderName,
    disableIncentives = false,
    disableTrade,
    enableOfferUpgrades = true,
    enablePriceDetailsInOfferBreakdown = true,
    oemLabels = {},
    enableLenderDisclaimerCTA = false,
    showMsrp = true
  } = useContext(ConfigContext) ?? {};
  const {
    manufacturerOffer: appliedOffers
  } = offerBreakdownProps.getPricingBreakdown(tabName);
  const {
    disclaimer: customOemDisclaimer
  } = oemLabels;
  const isFinance = tabName === 'finance';
  const isLease = tabName === 'lease';
  const isCash = tabName === 'cash';
  const {
    leasePayment,
    adjustedCapCost,
    showSpecialAPR,
    payment: financePayment,
    lender
  } = disclaimerProps[tabName] || {};
  const {
    owner = {}
  } = listing || {};
  const {
    privateSeller: isPSX
  } = owner;
  const lenderInterestRate = useCustomRate ? {
    custom: localInterestRate
  } : {
    [vehicleCondition]: localInterestRate
  };
  const lenderName = determineLender(customRate, lender);
  const isDRAccelerate = digitalRetailingType === 'accelerate';

  // Handler for onHide in Lending Options Modal
  const handleLendingOptionsModalHide = () => {
    setShowLendingOptionsModal(false);
  };
  const handleOfferBreakdownModalHide = () => {
    setShowOfferBreakdownModal(false);
  };

  // Determine which lending option is currently selected to preselect in modal
  const getLendingOptionIndex = useMemo(() => {
    if (typeof customRate === 'number' && customRate >= 0) return -1;
    const personalizedPayments = (paymentCalculationDetails || listingPaymentDetails)?.rates?.[getObfuscatedCredit(selectedCreditTier)]?.paymentOffers?.[selectedLoanTerm] ?? [];
    if (personalizedPayments.length) {
      const currentSelectionIndex = personalizedPayments.findIndex(option => option.offeredBy === lenderName && option[vehicleCondition] === localInterestRate);
      return currentSelectionIndex >= 0 ? currentSelectionIndex : 0;
    }
    return 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customRate, offerPrice, paymentCalculationDetails, listingPaymentDetails, selectedCreditTier, selectedLoanTerm, lenderName, localInterestRate]);
  const {
    onClick = () => {},
    handleSACACreditAppCTA
  } = dealerFinancingCTA || {};
  const updatedDealerFinancingCTA = {
    ...dealerFinancingCTA,
    onClick
  };
  if (handleSACACreditAppCTA) {
    updatedDealerFinancingCTA.onClick = () => {
      handleSACACreditAppCTA();
      setShowLendingOptionsModal(false);
    };
  }

  /**
   * Renders the correct lender payment component based on the current tab and payment error state.
   *
   * @returns {React.ReactNode} The rendered lender payment component.
   */
  const renderLenderPayment = () => {
    let uiContext = 'calculator';
    if (paymentErrorState) {
      uiContext = 'error';
    } else if (withUDSRedesignRollout) {
      uiContext = 'withRedesign';
    }
    if (isLoading && !hasTaxesFeesModalOpen) {
      return null;
    }
    if (isCash || !paymentErrorState && isFinance && totalFinancing < 0) {
      return /*#__PURE__*/jsx(LenderPayment, {
        appliedOffers: appliedOffers,
        disableIncentives: disableIncentives,
        price: isFinance ? 0 : Math.max(0, totalCashOut),
        uiContext: "cash",
        callToAction: financingCallToAction,
        isPSX: isPSX
      }, "cashPayment");
    }
    return /*#__PURE__*/jsx(LenderPayment, {
      appliedOffers: appliedOffers,
      disableIncentives: disableIncentives,
      isPSX: isPSX,
      paymentsInfo: {
        isPennyPerfectPayment,
        totalEquity,
        ...(isFinance && {
          downPayment: financeDownPayment,
          loanTerm: selectedLoanTerm,
          interestRate: {
            ...lenderInterestRate
          },
          taxesFees: totalTax,
          showSpecialAPR
        }),
        ...(isLease && {
          downPayment: dueAtSigningValue,
          leaseTerm: selectedLeaseTerm,
          leaseMileageRange: selectedMileage
        })
      },
      price: totalFinancing <= 0 ? 0 : localPrice,
      dealType: isLease ? 'lease' : undefined,
      enableLenderDisclaimerCTA: enableLenderDisclaimerCTA,
      interestRate: isFinance ? localInterestRate : undefined,
      vehicleCondition: vehicleCondition,
      uiContext: uiContext,
      lender: isFinance ? lenderName : undefined,
      showLenderName: showLenderName,
      personalizedPayment: isLease ? leasePayment : financePayment,
      showLenderDisclaimer: isCash ? false : showLenderDisclaimer,
      callToAction: financingCallToAction,
      handleFallbackPayment: isFinance ? handleFallbackPayment : undefined
    }, "lendingPayment");
  };
  return /*#__PURE__*/jsxs(Fragment, {
    children: [isFinance && !isDRAccelerate && showLendingOptionsModal && /*#__PURE__*/jsx(LendingOptionsModal, {
      container: container,
      onSubmit: handleLendingOptionsSelection,
      onHide: handleLendingOptionsModalHide,
      options: lendingOptions,
      disclaimer: showLenderDisclaimer ? financingDisclaimers.defaultLenderDisclaimer : undefined,
      initialCustomRate: customRate?.toString() ?? '',
      initialSelection: getLendingOptionIndex,
      customPaymentDetails: {
        price: parseInt(localPrice, 10),
        loanTerm: Number.parseInt(selectedLoanTerm, 10),
        downPayment: financeDownPayment,
        totalEquity,
        taxesFees: totalTax
      },
      secondaryCTA: updatedDealerFinancingCTA
    }, "lendingOptionsModal"), withUDSRedesignRollout && showOfferBreakdownModal && /*#__PURE__*/jsx(OfferBreakdownModal, {
      container: container,
      isVisible: showOfferBreakdownModal,
      onHide: handleOfferBreakdownModalHide,
      tabName: tabName,
      renderTaxesAndFees: renderTaxesAndFees,
      dueAtSigningValue: isLease ? dueAtSigningValue : undefined,
      adjustedCapCost: isLease ? adjustedCapCost : undefined,
      downPayment: isFinance ? financeDownPayment : undefined,
      totalFinancing: isFinance ? totalFinancing : undefined,
      totalCashOut: isCash ? totalCashOut : undefined,
      offerBreakdownProps: offerBreakdownProps
    }), renderLenderPayment(), isLoading && !hasTaxesFeesModalOpen && /*#__PURE__*/jsx(Box, {
      className: "margin-bottom-5",
      borderColor: "neutral-secondary",
      backgroundColor: "transparent",
      children: /*#__PURE__*/jsx(LoadingSpinner, {
        className: "margin-vertical-6"
      })
    }), !withUDSRedesignRollout && !paymentErrorState && offerBreakdownProps && /*#__PURE__*/jsx(OfferBreakdown, {
      tabName: tabName,
      renderTaxesAndFees: renderTaxesAndFees,
      adjustedCapCost: isLease ? adjustedCapCost : undefined,
      downPayment: isFinance ? financeDownPayment : undefined,
      totalFinancing: isFinance ? totalFinancing : undefined,
      totalCashOut: isCash ? totalCashOut : undefined,
      disableIncentives: disableIncentives,
      disableTrade: disableTrade,
      enableOfferUpgrades: enableOfferUpgrades,
      enablePriceBreakdown: enablePriceDetailsInOfferBreakdown,
      showMsrp: showMsrp,
      ...offerBreakdownProps
    }), !withUDSRedesignRollout && ctas /* Calls to Action buttons */, customOemDisclaimer && /*#__PURE__*/jsx(DisclaimerWithModalContent, {
      block: true,
      container: container,
      modalText: customOemDisclaimer,
      modalTitle: "Disclaimer",
      modalTriggerText: "View Disclaimer",
      "data-cmp": "custom-oem-disclaimer"
    }), !paymentErrorState && !withUDSRedesignRollout && /*#__PURE__*/jsx(Disclaimer, {
      container: container,
      paymentInfo: disclaimerProps[tabName],
      type: tabName,
      "data-cmp": "disclaimer"
    })]
  });
}
/**
 *  Renders estimated finance/lease/cash payment, Offer Breakdown, CTAs, and disclaimer.
 *  Renders as the right column on non-mobile layouts and when the `isSingleColumn` config item is set to false.
 *
 * @component
 */
var OfferColumn$1 = /*#__PURE__*/memo(OfferColumn);
export { OfferColumn$1 as default };
